// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { CommonModule, DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, Inject, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { catchError, combineLatest, distinctUntilChanged, EMPTY, filter, finalize, first, firstValueFrom, forkJoin, from, map, Observable, of, shareReplay, switchMap, take, tap, throwError, withLatestFrom } from 'rxjs';
import { HceAssembleViewRequest } from '../../../interfaces/hce/hce-assemble-view';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HceAssembleViewService } from '../../../services/hce/hce-assemble-view/hce-assemble-view.service';
import { Analysis } from '../../../interfaces/analysis';
import { AssembleViewWasmFacade, installEventThrottling, PerfMonitorService, uninstallEventThrottling } from '../../../services/hce/wasm';
import { HceLoadingOverlayService } from '../../../services/hce/hce-loading-overlay/hce-loading-overlay.service';
import { MatDialog } from '@angular/material/dialog';
import { MessageModalComponent } from '../../../shared/message-modal/message-modal.component';
import { HceSvFilter } from '../../../interfaces/hce/hce-sv-filter';
import { normalizeHceSvFilter } from '../../../shared/hce/normalizers/hce-sv-filter/hce-sv-filter.normalizer';
import { deepEqual } from '../../../services/hce/hce-parser/utils';
import { HceSvFilterModalComponent, HceSvFilterModalData } from '../hce-sv-filter-modal/hce-sv-filter-modal.component';
import { countHceSvFilter } from '../../../shared/hce/utils/hce-sv-filter/hce-sv-filter.util';

@Component({
  selector: 'app-hce-assemble-view',
  standalone: true,
  imports: [
    CommonModule,
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './hce-assemble-view.component.html',
  styleUrls: ['./hce-assemble-view.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class HceAssembleViewComponent implements OnInit, AfterViewInit, OnDestroy {
  // ===== View refs =====
  @ViewChild('canvasHost', { static: true }) public hostRef!: ElementRef<HTMLElement>;

  // ===== Public state (used by template) =====
  public params$!: Observable<HceAssembleViewRequest>;
  public params: HceAssembleViewRequest | null = null;
  public analysis$!: Observable<Analysis>;
  public analysis: Analysis | null = null;
  public badge = {
    value: 0,
    hidden: true,
  } as { value: number, hidden: boolean };

  // ===== Template Getters =====
  public get displayAnalysisId(): number {
    return this.analysis?.AnalysisID ?? 0;
  }
  public get displaySampleName(): string {
    return this.analysis?.SampleName ?? 'back';
  }
  public get summarylink() {
    if (!this.analysis?.AnalysisID) return null;
    return this.router.createUrlTree(
      ['/analysis', 'hce', this.analysis.AnalysisID],
      { queryParams: { tab: 'summary' }}
    );
  }
  public get isInvertedDrawing(): boolean {
    return this.params?.direction == 1;
  }

  // ===== Private state =====
  private enc$!: Observable<string>;
  private canvas!: HTMLCanvasElement;
  private svFilter: HceSvFilter = {};
  private readonly listeners: Array<() => void> = [];

  private readonly handlers: any = {
    unBlock: () => { this.blocker.close(); },
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef,
    private readonly zone: NgZone,
    private readonly cd: ChangeDetectorRef,
    private readonly svc: HceAssembleViewService,
    private readonly wasm: AssembleViewWasmFacade,
    private readonly blocker: HceLoadingOverlayService,
    @Inject(DOCUMENT) private readonly doc: Document,
    private readonly perf: PerfMonitorService,
    private readonly dialog: MatDialog,
  ) {}

  // ===== Lifecycle =====
  public ngOnInit(): void {
    this.doc.body.classList.add('assemble-view');

    this.restoreSvFilterFromSession();

    this.enc$ = this.route.paramMap.pipe(
      map(params => params.get('enc')),
      filter((enc): enc is string => !!enc),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    );

    this.params$ = this.enc$.pipe(
      switchMap(enc => this.svc.getParams(enc)),
      tap(params => this.params = params),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.analysis$ = this.params$.pipe(
      map(({ analysisId }) => Number(analysisId)),
      switchMap(analysisId => this.svc.getAnalysis(analysisId)),
      tap(analysis => this.analysis = analysis),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // keep warm
    this.analysis$.pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(err => {
        this.openErrorModal(err, $localize`Failed to load analysis.`);
        return EMPTY;
      })
    ).subscribe();
  }

  public async ngAfterViewInit(): Promise<void> {
    this.createAndMountCanvas();
    await this.waitForCanvasReady();

    installEventThrottling();

    // Ensure header DOM exists (params + analysis first emission)
    await firstValueFrom(combineLatest([this.params$, this.analysis$]).pipe(first()));
    this.cd.detectChanges();
    await new Promise<void>(r => requestAnimationFrame(() => r()));
    this.cd.detach();

    this.blocker.open($localize`Preparing...`);
    this.blocker.resetTasks();
    this.blocker.startTask({
      id: 'assemble',
      label: $localize`Downloading assemble data...`,
    });
    this.blocker.startTask({
      id: 'sv',
      label: $localize`Downloading sv data...`,
    });

    this.perf.start({
      fps: true,
      longTask: true,
      logToConsole: false,
      paint: false,
    });

    // Re-init WASM when id changes, with latest params/analysis
    this.buildInitStream().subscribe({
      error: (err: any) => {
        console.error('[assemble-view] stream error:', err);
        try { this.blocker.close(); } catch {}
        this.openErrorModal(err, $localize`Failed to initialize the view.`);
      }
    });
  }

  public ngOnDestroy(): void {
    try {
      this.listeners.forEach(off => off());
    } catch (e) {
      console.warn('[assemble-view] failed to remove some listeners:', e);
    }

    try {
      this.blocker.close();
    } catch {}

    try {
      this.doc.body.classList.remove('assemble-view');
    } catch {}

    try { uninstallEventThrottling(); } catch {}
    try { this.perf.stop(); } catch {}
  }

  // ===== Data fetch / WASM init helpers =====
  private async yieldToBrowser(): Promise<void> {
    // Let the browser paint at least one frame
    await new Promise<void>(r => requestAnimationFrame(() => r()));
    // Then yield one more macrotask to avoid being coalesced with heavy work
    await new Promise<void>(r => setTimeout(r, 0));
  }

  private buildInitStream() {
    return this.enc$.pipe(
      // dispose previous instance first
      switchMap(enc =>
        from(this.wasm.dispose()).pipe(
          catchError(() => of(void 0)),
          map(() => enc)
        )
      ),
      // bind latest params for this id
      withLatestFrom(this.params$),
      // start wasm
      switchMap(([_, params]) =>
        from(
          this.wasm.prepare({
            contig: String(params.contig),
            cutid: String(params.cutId ?? ''),
            unBlock: () => {
              this.blocker.close();
            },
          })
        ).pipe(map(() => params))
      ),

      tap(() => this.wasm.registerCallback(this.handlers)),
      switchMap(params =>
        from(this.wasm.waitForExports(['readSVJSON', 'readAssemblyJSON'])).pipe(map(() => params))
      ),
      withLatestFrom(this.analysis$),
      tap(() => this.blocker.setTitle?.($localize`Downloading data...`)),
      switchMap(([params, analysis]) =>
        forkJoin({
          as: this.getAssembleData(params),
          sv: this.getSvData(params, analysis),
        }).pipe(
          switchMap(({ as, sv }) => {
            if (!as || !sv) {
              return throwError(() => new Error('Failed to load assemble or sv data'));
            }
            return of({ as, sv });
          }),
          tap(() => {
            this.blocker.setTitle?.($localize`Processing...`);
          }),
          switchMap(v => from(this.yieldToBrowser()).pipe(
            map(() => v)
          )),
          switchMap(({ as, sv }) => new Observable<void>(subscriber => {
            this.zone.runOutsideAngular(async () => {
              try {
                this.blocker.forceCompleteTasks?.(['assemble', 'sv']);
                await this.blocker.flush(2);
                if (as) {
                  this.wasm.callReadAssemblyJson(JSON.stringify(as));
                }
                if (sv) {
                  this.wasm.callReadSvJson(JSON.stringify(sv));
                }
                subscriber.next();
                subscriber.complete();
              } catch (e) {
                subscriber.error(e);
              }
            });
          }))
        )
      ),
      takeUntilDestroyed(this.destroyRef),
      finalize(async () => {
        // Release resources without touching body class here.
        try { await this.wasm.dispose(); } catch {}
        try { this.blocker.close(); } catch {}
      })
    );
  }

  private getAssembleData(params: HceAssembleViewRequest): Observable<any> {
    return this.svc.getAssembleData(
      Number(params.analysisId),
      Number(params.contig),
    ).pipe(
      tap(() => this.blocker.completeTask?.('assemble')),
      catchError(err => {
        this.blocker.errorTask?.('assemble');
        return of(null);
      }),
    );
  }

  private getSvData(params: HceAssembleViewRequest, analysis: Analysis): Observable<any> {
    return this.svc.getSvData(analysis, this.svFilter).pipe(
      map(svs => svs.filter(sv => sv.SVName == params.contig)),
      tap(() => this.blocker.completeTask?.('sv')),
      catchError(err => {
        this.blocker.errorTask?.('sv');
        return of(null);
      }),
    );
  }

  private getRequiredSvData(params: HceAssembleViewRequest, analysis: Analysis): Observable<any> {
    return this.getSvData(params, analysis).pipe(
      switchMap(sv =>
        sv == null
          ? throwError(() => new Error('Failed to load sv data.'))
          : of(sv)
      ),
    );
  }

  private reloadSvWithOverlay(): void {
    if (!this.params || !this.analysis) return;

    this.blocker.open($localize`Processing...`);
    this.blocker.resetTasks();
    this.blocker.startTask({
      id: 'sv',
      label: $localize`Downloading sv data...`,
    });

    this.getRequiredSvData(this.params, this.analysis).pipe(
      take(1),
      switchMap(sv =>
        new Observable<void>(subscriber => {
          this.zone.runOutsideAngular(() => {
            try {
              this.blocker.forceCompleteTasks?.(['sv']);

              this.wasm.callReadSvJson(JSON.stringify(sv));
              this.wasm.callRedraw();

              subscriber.next();
              subscriber.complete();
            } catch (e) {
              subscriber.error(e);
            }
          });
        })
      ),
      catchError(err => {
        this.blocker.errorTask?.('sv');
        this.openErrorModal(err, $localize`Failed to reload sv data.`);
        return EMPTY;
      }),
      finalize(() => {
        // Always close overlay on completion OR error
        this.blocker.close();
      }),
    ).subscribe();
  }

  private createAndMountCanvas() {
    this.zone.runOutsideAngular(() => {
      this.canvas = document.createElement('canvas');
      this.canvas.id = 'main_canvas';
      this.canvas.setAttribute('tabindex', '0');
      this.canvas.style.outline = 'none';
      this.canvas.style.touchAction = 'none';

      // focus on pointer/mouse down so key events reach canvas (WASM keyState)
      const focusCanvas = () => this.canvas.focus({ preventScroll: true });
      const opts: AddEventListenerOptions = { passive: true };
      this.canvas.addEventListener('pointerdown', focusCanvas, opts);
      this.canvas.addEventListener('mousedown', focusCanvas, opts);
      this.listeners.push(() => this.canvas.removeEventListener('pointerdown', focusCanvas, opts));
      this.listeners.push(() => this.canvas.removeEventListener('mousedown', focusCanvas, opts));

      // place before host (same as current)
      this.hostRef.nativeElement.before(this.canvas);
    });
  }

  private async waitForCanvasReady(
    selector = '#main_canvas',
    timeoutMs = 2000
  ): Promise<HTMLCanvasElement> {
    const start = performance.now();

    await new Promise<void>(r => requestAnimationFrame(() => r()));

    let el = this.doc.querySelector<HTMLCanvasElement>(selector);
    while(!(el && el.isConnected)) {
      if (performance.now() - start > timeoutMs) {
        throw new Error(`Canvas (${selector}) not found or not attached to the DOM within ${timeoutMs} ms.`);
      }
      await new Promise<void>(r => setTimeout(r, 16));
      el = this.doc.querySelector<HTMLCanvasElement>(selector);
    }

    const ctx = (el.getContext('2d') || el.getContext('bitmaprenderer'));
    if (!ctx) {
      await new Promise<void>(r => requestAnimationFrame(() => r()));
      if (!(el.getContext('2d') || el.getContext('bitmaprenderer'))) {
        console.warn(
          'Failed to acquire a canvas rendering context (2D/bitmaprenderer). ' +
          'This can happen due to timing. If WASM initializes the context later, it is safe to ignore.'
        );
      }
    }
    return el;
  }

  private runWithChangeDetection(fn: () => void): void {
    this.cd.reattach();
    try {
      fn();
      this.cd.detectChanges();
    } finally {
      this.cd.detach();
    }
  }

  // ===== Filter restore / badge helpers =====
  private restoreSvFilterFromSession(): void {
    try {
      const value = sessionStorage.getItem('hceSvFilter');
      const filter = value
        ? normalizeHceSvFilter(JSON.parse(value))
        : normalizeHceSvFilter({});
      filter.matchAnnotation = undefined;
      this.svFilter = filter;
    } catch {
      this.svFilter = normalizeHceSvFilter({});
    }
    this.runWithChangeDetection(() => {
      this.updateFilterBadge(this.svFilter);
    });
  }

  private applySvFilter(filter: HceSvFilter): void {
    this.svFilter = filter;
    this.runWithChangeDetection(() => {
      this.updateFilterBadge(filter);
    });
  }

  private updateFilterBadge(filter?: HceSvFilter): void {
    if (!filter) {
      this.badge.value = 0;
      this.badge.hidden = true;
      return;
    }

    const count = countHceSvFilter(filter);

    this.badge.value = count;
    this.badge.hidden = count === 0;
  }

  // ===== UI handlers =====
  onCopyLinkClick(event: Event) {
    navigator.clipboard.writeText(location.href);
    const balloon = this.doc.getElementById('copyBalloon');
    if (balloon) {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      balloon.style.left = (rect.right + window.scrollX + 8) + 'px';
      balloon.style.top = (rect.top + window.scrollY) + 'px';
      balloon.style.display = 'block';
      setTimeout(() => {
        balloon.style.display = 'none';
      }, 1200);
    }
  }

  onFilterClick() {
    this.dialog.open<
      HceSvFilterModalComponent,
      HceSvFilterModalData,
      { filter: HceSvFilter; count: number; }
    >(HceSvFilterModalComponent, {
      maxWidth: 'none',
      width: '1200px',
      height: '80vh',
      autoFocus: 'dialog',
      restoreFocus: true,
      data: {
        svFilter: this.svFilter,
        annotationFiles: [],
        refNameMap: this.svc.refsNameMapping,
      } satisfies HceSvFilterModalData,
    }).afterClosed().subscribe({
      next: (value) => {
        if (!value) return;

        const filter = normalizeHceSvFilter(value.filter);
        if (deepEqual(this.svFilter, filter)) return;

        try {
          sessionStorage.setItem('hceSvFilter', JSON.stringify(filter));
        } catch {}

        this.applySvFilter(filter);
        this.reloadSvWithOverlay();
      }
    });
  }

  // ===== Error modal helper =====
  private errorModalArmed = false;

  private openErrorModal(err: unknown, message = $localize`Unknown error occurred.`): void {
    console.error('[error modal]', err);

    // Prevent stacking multiple dialogs if errors race in
    if (this.errorModalArmed) {
      // Optionally update or re-open message, but avoid adding another afterClosed subscription
      return;
    }
    this.errorModalArmed = true;

    try {
      const ref = this.dialog.open(MessageModalComponent, {
        width: '800px',
        data: {
          title: $localize`Error`,
          message,
        }
      });
      ref.afterClosed()
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.handleFatalCloseOrRedirect());
    } catch {
      // Avoid throwing from the error handler
    }
  }

  private handleFatalCloseOrRedirect(): void {
    try {
      if (window.opener && !window.opener.closed) {
        // In some browsers, window.close() is allowed only for script-opened windows.
        // If the close is blocked, you may still be on this page; fallback below will handle.
        window.close();
        return;
      }
      this.router.navigateByUrl('/').finally(() => {
        try { this.doc.body.classList.remove('assemble-view'); } catch {}
      });
    } finally {
      // Allow re-arming for subsequent errors (e.g., user re-opened this view)
      this.errorModalArmed = false;
    }
  }
}
